import UDate from "@repo/utils/UDate";
import FeatureStore from "@repo/utils-solid/FeatureStore";

import { Organization } from "./organizations";
import { Get } from "@repo/utils/api";
import Duration from "@repo/utils/Duration";
import { api } from "..";
import StorePair from "@repo/utils-solid/StorePair";
import UArray from "@repo/utils/UArray";
import Range from "@repo/utils/Range";

export namespace Metrics {
  export type Query = {
    range: UDate.NoTime.Range;
    key: Key;
  };

  export type Key =
    | "gross_sales"
    | "new_customers"
    | "purchased_memberships"
    | "recurring_memberships"
    | "total_bookings"
    | "total_check_ins";
  export type Data = Data.Point[];
  export namespace Data {
    export type Point = {
      x: UDate.NoTime;
      y: number | null;
    };
  }
}

export namespace MetricsRest {
  export type Organization = {
    [key in Metrics.Key]: Get<
      {
        startDate: Date;
        endDate: Date;
        intervalType: Duration.Unit;
        intervalCount: number;
      },
      { data: { xValues: number[]; yValues: number[] } }
    >;
  };
}

const initialState = {
  dataByOrganizationIdByRangeByKey: {} as {
    [id: Organization.Id]: {
      [key in Metrics.Key]?: {
        [range: UDate.NoTime.Range]: Metrics.Data;
      };
    };
  },
};

export const { Context, Provider, useMetrics } = FeatureStore.init(
  "Metrics",
  initialState,
  ([store, setStore], { withLogger, attachAction }) => {
    const accessors = {
      data: Object.assign(
        attachAction(
          (orgId: Organization.Id, query: Metrics.Query) =>
            store.dataByOrganizationIdByRangeByKey[orgId]?.[query.key]?.[
              query.range
            ],
          (orgId, query) => actions.fetchMetrics(orgId, query),
        ),
        {
          total: attachAction(
            (orgId: Organization.Id, query: Metrics.Query) =>
              UArray.sum(
                store.dataByOrganizationIdByRangeByKey[orgId]?.[query.key]?.[
                  query.range
                ] || [],
                (point) => point.y ?? 0,
              ),
            (orgId, query) => actions.fetchMetrics(orgId, query),
          ),
        },
      ),
    };

    const mutations = {};

    const actions = withLogger("action", {
      fetchMetrics(organizationId: Organization.Id, params: Metrics.Query) {
        const [startDate, _endDate] = UArray.map(
          UDate.NoTime.Range.toPair(params.range),
          UDate.NoTime.toDate,
        );
        const endDate = UDate.add.days(_endDate, 1);

        return api.organizations[organizationId]!["metrics"][params.key].GET({
          startDate,
          endDate,
          intervalType: "day",
          intervalCount: 1,
        }).then(({ data }) => {
          const yByX = Object.fromEntries(
            data.xValues.map((x, i) => [
              UDate.NoTime.from(new Date(x)),
              data.yValues[i]!,
            ]),
          );
          const today = UDate.startOf.day(new Date());
          StorePair.safeSetter([store, setStore])(
            "dataByOrganizationIdByRangeByKey",
            organizationId,
            params.key,
            params.range,
            Range(UDate.diff.days(endDate, startDate))
              .map((n) => UDate.NoTime.from(UDate.add.days(startDate, n)))
              .map((date) => ({
                x: date,
                y:
                  UDate.NoTime.toDate(date).getTime() > today.getTime()
                    ? null
                    : yByX[date] ?? 0,
              })),
          );
        });
      },
    });

    return { accessors, actions };
  },
);
